import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

export const FREE_GIFT_PROPERTY = '_mg_free_gift';
const DECLINED_ATTRIBUTE = '_mg_free_gift_declined';
const EVENT_SOURCE = 'cart-free-gift';

/**
 * @param {{ properties?: Record<string, unknown> | null }} item
 * @returns {boolean}
 */
export function isFreeGiftLine(item) {
  const value = item?.properties?.[FREE_GIFT_PROPERTY];
  return value === 'true' || value === true;
}

/**
 * Cart value after discounts, excluding the free gift itself.
 * @param {{ total_price?: number, items?: Array<{ final_line_price?: number, properties?: Record<string, unknown> | null }> }} cart
 * @returns {number}
 */
export function getQualifyingTotal(cart) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const giftTotal = items
    .filter(isFreeGiftLine)
    .reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);

  return Math.max(Number(cart?.total_price || 0) - giftTotal, 0);
}

class CartFreeGift extends HTMLElement {
  /** @type {boolean | null} */
  #hadGiftLine = null;
  #syncing = false;
  #pending = false;
  #failedAddKey = '';

  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#sync();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #handleCartUpdate = (event) => {
    if (event?.detail?.data?.source === EVENT_SOURCE) return;
    this.#sync();
  };

  async #sync() {
    if (this.#syncing) {
      this.#pending = true;
      return;
    }

    this.#syncing = true;

    try {
      do {
        this.#pending = false;
        await this.#applyRules(await this.#fetchCart());
      } while (this.#pending);
    } catch (error) {
      console.warn('[cart-free-gift]', error);
    } finally {
      this.#syncing = false;
    }
  }

  /**
   * @param {any} cart
   */
  async #applyRules(cart) {
    const giftLines = this.#getGiftLines(cart);
    const qualifies = getQualifyingTotal(cart) >= this.#threshold;
    const declined = cart.attributes?.[DECLINED_ATTRIBUTE] === 'true';
    const removedByCustomer = this.#hadGiftLine === true && giftLines.length === 0 && qualifies;

    this.#hadGiftLine = giftLines.length > 0;

    if (!qualifies) {
      if (giftLines.length > 0) await this.#setLineQuantities(giftLines.map((line) => [line.key, 0]));
      if (declined) await this.#setDeclined(false);
      this.#hadGiftLine = false;
      return;
    }

    if (removedByCustomer) {
      await this.#setDeclined(true);
      return;
    }

    if (giftLines.length === 0) {
      if (!declined) await this.#addGift(cart);
      return;
    }

    const [primary, ...extra] = giftLines;
    const changes = extra.map((line) => [line.key, 0]);
    if (primary.quantity !== 1) changes.push([primary.key, 1]);
    if (changes.length > 0) await this.#setLineQuantities(changes);
  }

  /**
   * @param {any} cart
   */
  async #addGift(cart) {
    const addKey = `${this.#variantId}:${cart.total_price}`;
    if (this.#failedAddKey === addKey) return;

    try {
      await this.#mutate(Theme.routes.cart_add_url, {
        items: [{ id: this.#variantId, quantity: 1, properties: { [FREE_GIFT_PROPERTY]: 'true' } }],
      });
      this.#hadGiftLine = true;
      this.#failedAddKey = '';
    } catch (error) {
      this.#failedAddKey = addKey;
      throw error;
    }
  }

  /**
   * @param {Array<[string, number]>} changes - [line item key, quantity] pairs
   */
  async #setLineQuantities(changes) {
    await this.#mutate(Theme.routes.cart_update_url, { updates: Object.fromEntries(changes) });
  }

  /**
   * @param {boolean} declined
   */
  async #setDeclined(declined) {
    await this.#mutate(Theme.routes.cart_update_url, {
      attributes: { [DECLINED_ATTRIBUTE]: declined ? 'true' : '' },
    });
  }

  /**
   * @param {string} url
   * @param {Record<string, unknown>} payload
   */
  async #mutate(url, payload) {
    const body = JSON.stringify({
      ...payload,
      sections: this.#sectionsToUpdate,
      sections_url: window.location.pathname,
    });
    const response = await fetch(url, fetchConfig('json', { body }));
    const data = await response.json();

    if (!response.ok || data.status || data.errors) throw data;

    const cart = await this.#fetchCart();
    this.dispatchEvent(
      new CartUpdateEvent(cart, this.id || EVENT_SOURCE, {
        source: EVENT_SOURCE,
        itemCount: cart.item_count,
        sections: data.sections || {},
      })
    );
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    const cart = await response.json();

    if (!response.ok || cart.status) throw cart;

    return cart;
  }

  /**
   * @param {any} cart
   */
  #getGiftLines(cart) {
    return Array.isArray(cart.items) ? cart.items.filter(isFreeGiftLine) : [];
  }

  get #sectionsToUpdate() {
    const sections = new Set();

    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sections.add(component.dataset.sectionId);
      }
    }

    return Array.from(sections).join(',');
  }

  get #variantId() {
    return Number(this.dataset.giftVariantId || 0);
  }

  get #threshold() {
    return Number(this.dataset.giftThreshold || 0);
  }
}

if (!customElements.get('cart-free-gift')) {
  customElements.define('cart-free-gift', CartFreeGift);
}
